import { Container, Alert, Text, Button, Group } from "@mantine/core";
import { IconInfoCircle, IconArrowRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";

interface RouteMovedNoticeProps {
  /** What used to live here, in the user's words. */
  what: string;
  /** Where it lives now. */
  to: string;
  /** Label for the destination. */
  toLabel: string;
}

/**
 * US-177 (F-131) — a removed route that says so.
 *
 * The audit portal had routes removed during the IA change (settings/auditor-group,
 * settings/invitation-requests, replaced by the Auditors page). Nothing was left
 * behind: the paths fall through to the global `path: "*"` catch-all in
 * routes.tsx, which bounces to /login. Someone following a bookmark or an old
 * link was silently moved somewhere else and told nothing — indistinguishable
 * from a permission refusal or a broken app.
 *
 * A redirect is the right behaviour for a path that has a direct equivalent
 * (see group-homes -> /audit/access?tab=find-organization, which is deliberate).
 * This is for the case where the page is genuinely GONE and its replacement is
 * somewhere the user has to choose to go.
 */
export function RouteMovedNotice({ what, to, toLabel }: RouteMovedNoticeProps) {
  return (
    <Container size="sm" px={0} py="xl">
      <Alert
        icon={<IconInfoCircle size={18} />}
        title="This page has moved"
        color="blue"
        variant="light"
      >
        <Text size="sm" mb="md">
          {what} is no longer here. It is now part of {toLabel}.
        </Text>
        <Group>
          <Button
            component={Link}
            to={to}
            size="xs"
            rightSection={<IconArrowRight size={14} />}
          >
            Go to {toLabel}
          </Button>
        </Group>
      </Alert>
    </Container>
  );
}
